import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  MapPin,
  Calendar,
  Clock,
  Users,
  Trophy,
  Zap,
  Target,
  TrendingDown,
  Thermometer,
  Droplets,
  Wind,
  Cloud,
  CloudRain,
  Sun,
  CloudLightning,
  CloudSnow,
  Flag,
  Timer as TimerIcon,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { API } from '../config/api';

const formatLap = (seconds) => {
  if (!seconds || seconds <= 0) return '—';
  const mins = Math.floor(seconds / 60);
  const secs = (seconds % 60).toFixed(3);
  return `${mins}:${secs.padStart(6, '0')}`;
};

const formatGap = (gap) => {
  if (gap == null || isNaN(gap)) return '—';
  const sign = gap >= 0 ? '+' : '−';
  return `${sign}${Math.abs(gap).toFixed(3)}s`;
};

const formatDateES = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString('es-AR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
};

const weatherIcon = (condition) => {
  const c = (condition || '').toLowerCase();
  if (c.includes('tormenta')) return <CloudLightning className="w-5 h-5 text-yellow-300" />;
  if (c.includes('lluvia') || c.includes('chaparr') || c.includes('llovizna'))
    return <CloudRain className="w-5 h-5 text-blue-400" />;
  if (c.includes('nevada')) return <CloudSnow className="w-5 h-5 text-white" />;
  if (c.includes('nub')) return <Cloud className="w-5 h-5 text-gray-400" />;
  return <Sun className="w-5 h-5 text-yellow-400" />;
};

const Stat = ({ icon: Icon, label, value, sub, tone = 'default' }) => {
  const toneClass =
    tone === 'gold'
      ? 'text-yellow-400'
      : tone === 'red'
      ? 'text-red-400'
      : tone === 'green'
      ? 'text-green-400'
      : 'text-white';
  return (
    <div className="bg-neutral-900 border border-neutral-800 rounded-lg p-3">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-gray-500 mb-1">
        {Icon && <Icon className="w-3 h-3" />}
        {label}
      </div>
      <div className={`text-lg font-bold tabular-nums ${toneClass}`}>{value}</div>
      {sub && <div className="text-[11px] text-gray-400 mt-0.5">{sub}</div>}
    </div>
  );
};

const SessionOverview = ({ race }) => {
  const [weather, setWeather] = useState(null);
  const [weatherLoading, setWeatherLoading] = useState(false);

  useEffect(() => {
    if (!race?.id) return;
    let cancelled = false;
    setWeatherLoading(true);
    axios
      .get(`${API}/races/${race.id}/weather`)
      .then((r) => {
        if (!cancelled) setWeather(r.data);
      })
      .catch(() => {
        if (!cancelled) setWeather(null);
      })
      .finally(() => {
        if (!cancelled) setWeatherLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [race?.id]);

  // Derived statistics
  const stats = useMemo(() => {
    const drivers = (race?.drivers || []).filter((d) => d && d.best_lap > 0);
    if (drivers.length === 0) return null;
    const sorted = [...drivers].sort((a, b) => a.best_lap - b.best_lap);
    const fastest = sorted[0];
    const slowest = sorted[sorted.length - 1];
    const gapLast = slowest.best_lap - fastest.best_lap;
    const medianLap = sorted[Math.floor(sorted.length / 2)].best_lap;

    // Optimal lap: sum of best time per sector across all drivers
    const sectorCount = Math.max(0, ...drivers.map((d) => (d.sector_times || []).length));
    const bestSectors = Array.from({ length: sectorCount }, (_, i) => {
      let best = null;
      let holder = null;
      drivers.forEach((d) => {
        const st = d.sector_times?.[i];
        if (!st || !Array.isArray(st.times) || st.times.length === 0) return;
        const min = Math.min(...st.times.filter((t) => t > 0));
        if (best === null || min < best) {
          best = min;
          holder = d;
        }
      });
      return { index: i + 1, time: best, holder };
    });
    const optimalLap = bestSectors.every((s) => s.time != null)
      ? bestSectors.reduce((acc, s) => acc + s.time, 0)
      : null;
    const gapOptimalToFastest =
      optimalLap != null ? fastest.best_lap - optimalLap : null;

    return {
      fastest,
      slowest,
      sorted,
      gapLast,
      medianLap,
      bestSectors,
      optimalLap,
      gapOptimalToFastest,
      totalDrivers: drivers.length,
    };
  }, [race]);

  if (!race) return null;

  const topList = (stats?.sorted || []).slice(0, 5);

  return (
    <div className="space-y-3" data-testid="session-overview">
      {/* Header */}
      <Card className="bg-gradient-to-br from-neutral-900 via-neutral-900 to-red-950/30 border-red-900/40">
        <CardContent className="pt-5 pb-4">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex-1 min-w-0">
              <Badge className="bg-red-600 text-white mb-2" data-testid="session-category">
                {race.category?.replace('_', ' ').toUpperCase() || 'SESIÓN'}
              </Badge>
              <h3 className="text-xl font-bold text-white leading-tight" data-testid="session-name">
                {race.name}
              </h3>
            </div>
            <div className="text-right shrink-0">
              <div className="flex items-center gap-1.5 text-gray-300 text-sm">
                <Users className="w-4 h-4 text-red-400" />
                <span className="font-semibold text-white">{stats?.totalDrivers ?? 0}</span>
                <span className="text-gray-500 text-xs">pilotos</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
            <div className="flex items-center gap-2 text-gray-400">
              <MapPin className="w-4 h-4 text-red-400 shrink-0" />
              <span className="truncate" title={race.circuit || race.location || ''}>
                {race.circuit || race.location || 'Circuito no detectado'}
              </span>
            </div>
            <div className="flex items-center gap-2 text-gray-400">
              <Calendar className="w-4 h-4 text-red-400 shrink-0" />
              <span>{formatDateES(race.date)}</span>
            </div>
            <div className="flex items-center gap-2 text-gray-400">
              <Clock className="w-4 h-4 text-red-400 shrink-0" />
              <span>{race.session_time ? `${race.session_time} hs` : 'Hora no detectada'}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Key stats grid */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <Stat
            icon={Zap}
            label="Vuelta más rápida"
            value={formatLap(stats.fastest.best_lap)}
            sub={`#${stats.fastest.driver_number} ${stats.fastest.driver_name}`}
            tone="gold"
          />
          <Stat
            icon={Target}
            label="Vuelta óptima"
            value={stats.optimalLap ? formatLap(stats.optimalLap) : '—'}
            sub={
              stats.gapOptimalToFastest != null
                ? `${formatGap(-stats.gapOptimalToFastest)} vs mejor`
                : 'sectores incompletos'
            }
            tone="green"
          />
          <Stat
            icon={TrendingDown}
            label="Gap líder → último"
            value={formatGap(stats.gapLast)}
            sub={`#${stats.slowest.driver_number} ${stats.slowest.driver_name}`}
            tone="red"
          />
          <Stat
            icon={TimerIcon}
            label="Mediana del campo"
            value={formatLap(stats.medianLap)}
            sub={`${stats.totalDrivers} pilotos ordenados`}
          />
        </div>
      )}

      {/* Best sectors */}
      {stats?.bestSectors?.length > 0 && (
        <Card className="bg-neutral-900 border-neutral-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-white flex items-center gap-2">
              <Flag className="w-4 h-4 text-red-500" />
              Mejores sectores de la sesión
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-2">
              {stats.bestSectors.map((s) => (
                <div
                  key={s.index}
                  className="bg-neutral-950 border border-neutral-800 rounded-md p-2"
                  data-testid={`best-sector-${s.index}`}
                >
                  <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">
                    Sector {s.index}
                  </div>
                  <div className="text-base font-bold text-white tabular-nums">
                    {s.time != null ? s.time.toFixed(3) + 's' : '—'}
                  </div>
                  {s.holder && (
                    <div className="text-[11px] text-gray-400 mt-0.5 truncate">
                      #{s.holder.driver_number} {s.holder.driver_name}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Top 5 */}
      {topList.length > 0 && (
        <Card className="bg-neutral-900 border-neutral-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-white flex items-center gap-2">
              <Trophy className="w-4 h-4 text-yellow-400" />
              Top 5 — Vuelta más rápida
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1" data-testid="session-top5">
              {topList.map((d, idx) => {
                const gap = d.best_lap - topList[0].best_lap;
                return (
                  <div
                    key={d.driver_number}
                    className="flex items-center justify-between text-sm bg-neutral-950/60 px-3 py-1.5 rounded"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span
                        className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                          idx === 0
                            ? 'bg-yellow-500 text-black'
                            : idx === 1
                            ? 'bg-gray-400 text-black'
                            : idx === 2
                            ? 'bg-amber-700 text-white'
                            : 'bg-neutral-800 text-gray-400'
                        }`}
                      >
                        {idx + 1}
                      </span>
                      <span className="text-gray-500 text-xs shrink-0">#{d.driver_number}</span>
                      <span className="text-white truncate">{d.driver_name}</span>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-white font-mono tabular-nums">
                        {formatLap(d.best_lap)}
                      </span>
                      <span
                        className={`text-xs font-mono tabular-nums w-16 text-right ${
                          idx === 0 ? 'text-yellow-400' : 'text-gray-400'
                        }`}
                      >
                        {idx === 0 ? 'Líder' : formatGap(gap)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Weather */}
      <Card className="bg-neutral-900 border-neutral-800">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-white flex items-center gap-2">
            <Thermometer className="w-4 h-4 text-orange-400" />
            Condiciones de la sesión
          </CardTitle>
        </CardHeader>
        <CardContent>
          {weatherLoading ? (
            <div className="text-xs text-gray-500">Cargando clima histórico…</div>
          ) : !weather ? (
            <div className="text-xs text-gray-500">
              Clima no disponible para esta sesión (sin fecha/ubicación resuelta).
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="session-weather">
              <div className="flex items-center gap-2 bg-neutral-950 rounded-md p-2">
                {weatherIcon(weather.weather_condition)}
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-gray-500">Condición</div>
                  <div className="text-sm font-semibold text-white capitalize">
                    {weather.weather_condition || '—'}
                  </div>
                </div>
              </div>
              <div className="bg-neutral-950 rounded-md p-2">
                <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-gray-500 mb-0.5">
                  <Thermometer className="w-3 h-3" /> Temp. amb.
                </div>
                <div className="text-base font-bold text-white tabular-nums">
                  {weather.temperature_c != null ? `${weather.temperature_c.toFixed(1)}°C` : '—'}
                </div>
                {weather.track_temp_estimate_c != null && (
                  <div className="text-[11px] text-gray-400">
                    Pista ≈ {weather.track_temp_estimate_c.toFixed(0)}°C
                  </div>
                )}
              </div>
              <div className="bg-neutral-950 rounded-md p-2">
                <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-gray-500 mb-0.5">
                  <Droplets className="w-3 h-3" /> Humedad
                </div>
                <div className="text-base font-bold text-white tabular-nums">
                  {weather.humidity_pct != null ? `${weather.humidity_pct.toFixed(0)}%` : '—'}
                </div>
              </div>
              <div className="bg-neutral-950 rounded-md p-2">
                <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-gray-500 mb-0.5">
                  <Wind className="w-3 h-3" /> Viento
                </div>
                <div className="text-base font-bold text-white tabular-nums">
                  {weather.wind_speed_kmh != null ? `${weather.wind_speed_kmh.toFixed(0)} km/h` : '—'}
                </div>
                {weather.wind_direction_cardinal && (
                  <div className="text-[11px] text-gray-400">
                    Desde el {weather.wind_direction_cardinal}
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <p className="text-center text-xs text-gray-500 pt-1">
        Seleccioná una acción en el panel izquierdo (Comparar, Parciales, Vuelta x Vuelta, Consistencia o Reporte) para profundizar.
      </p>
    </div>
  );
};

export default SessionOverview;
